import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import {
  AdvancedSearchControl,
  DatagridQuickFilterResetType,
  QuickFilterPanel,
  getSetTranslateAdvancedSearch,
} from '@ingenico/react-ui/advanced-search';
import { Banner } from '@ingenico/react-ui/banner';
import { CoreContainer } from '@ingenico/react-ui/core-container';
import {
  DATAGRID_ROWS_PER_PAGE_OPTIONS,
  DataGridCore,
  ListViewContainer,
  ListViewHeader,
} from '@ingenico/react-ui/datagrid';
import { DateRange } from '@ingenico/react-ui/date-range';
import { NoticeCard } from '@ingenico/react-ui/notice-card';
import { SearchBarInput } from '@ingenico/react-ui/search-bar-input';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton, LinearProgress, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './logicalDeviceHistoryTab.module.scss';
import { useLogicalDeviceHistoryTab } from './useLogicalDeviceHistoryTab.vm';

export function LogicalDeviceHistoryTab() {
  const {
    checkAllowReset,
    overrideDefaultFilterTypes,
    quickFilterCall,
    handleFilterChange,
    filterRefApi,
    isLoadingQuickFilters,
    fields,
    setDebouncedSearchQuery,
    getLabelQuickFilterLabel,
    columns,
    filteredDeviceCallLogsData,
    deviceCallLogsQuery,
    gridProps,
    errorMessage,
    searchBarPlaceholder,
    dateFilter,
    isFirstLoading,
    noRowsProps,
    userLocale,
    userDateTimeFormat,
    errorStatus,
    errorTitle,
    handleRefresh,
  } = useLogicalDeviceHistoryTab();

  const getDeviceHistoryComponent = useCallback(() => {
    switch (errorStatus) {
      case 404: {
        return <NoticeCard image="not-found" isBordered message={errorMessage} title={errorTitle} />;
      }
      default: {
        return <Banner message={errorMessage} />;
      }
    }
  }, [errorMessage, errorStatus, errorTitle]);
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON]);

  const { quickFilterPanelTexts, advancedControlTexts } = getSetTranslateAdvancedSearch(t);
  return (
    <>
      {isFirstLoading && <LinearProgress />}
      {deviceCallLogsQuery.isError && errorMessage && <CoreContainer>{getDeviceHistoryComponent()}</CoreContainer>}
      {gridProps?.shouldDisplayGrid && (
        <CoreContainer>
          <ListViewContainer>
            <ListViewHeader
              leftSlot={
                <Box className={styles.leftSlotContainer}>
                  <Box className={styles.leftSlot__box}>
                    <SearchBarInput
                      i18nNamespaces={[ALL_LNGS_NAMESPACES.COMMON]}
                      onSearchChange={(value) => setDebouncedSearchQuery(value)}
                      placeholder={searchBarPlaceholder}
                      variant="filled"
                    />
                    <AdvancedSearchControl
                      advancedSearchProposals={[]}
                      checkAllowReset={checkAllowReset}
                      fields={fields}
                      getFilterConditionLabel={(filter) => `
                      ${t(filter.field)}
                      ${t(`common:advancedSearch.conditionValues.${filter.condition}`)}
                      ${String(filter.criteria)}
                    `}
                      getOperatorsLabel={(operator) => t(`common:advancedSearch.conditionValues.${operator}`)}
                      {...advancedControlTexts}
                      getQuickFilterLabel={getLabelQuickFilterLabel}
                      isLoading={isLoadingQuickFilters}
                      onQuickFilterChange={handleFilterChange}
                      overrideDefaultFilterTypes={overrideDefaultFilterTypes}
                      overrideResetFieldType={{
                        status: DatagridQuickFilterResetType.FloatingButton,
                      }}
                      quickFiltersOptionListRequests={{
                        status: quickFilterCall,
                      }}
                      ref={filterRefApi}
                    >
                      <QuickFilterPanel {...quickFilterPanelTexts} searchbarEndAdornment={null} />
                    </AdvancedSearchControl>
                  </Box>
                  <DateRange
                    customClassName={styles.dateRange}
                    dateTimeFormat={userDateTimeFormat}
                    endDate={dateFilter.endDate}
                    locale={userLocale}
                    onChange={dateFilter?.setDateRange}
                    startDate={dateFilter.startDate}
                  />
                  <Tooltip title={t('button.refresh', { ns: ALL_LNGS_NAMESPACES.COMMON })}>
                    <IconButton
                      className={styles.refreshButton}
                      disabled={isLoadingQuickFilters || deviceCallLogsQuery.isLoading}
                      onClick={handleRefresh}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              leftSlotClassName={styles.logicalDeviceHistoryTab__leftSlotClassName}
            />
            <DataGridCore
              apiRef={gridProps.datagridRef}
              classes={{ row: styles.row }}
              columnBufferPx={5}
              columns={columns}
              getRowId={gridProps.getRowId}
              headerHeightPixels={395}
              initialState={{
                pagination: { paginationModel: { pageSize: DATAGRID_ROWS_PER_PAGE_OPTIONS[0], page: 0 } },
              }}
              loading={deviceCallLogsQuery.isLoading}
              noRowsProps={noRowsProps}
              onPaginationModelChange={gridProps.handlePaginationModelChange}
              onRowClick={gridProps.handleRowClick}
              onSortModelChange={gridProps.handleSortModelChange}
              paginationMode="server"
              rowCount={gridProps.total}
              rows={filteredDeviceCallLogsData}
              pageSizeOptions={DATAGRID_ROWS_PER_PAGE_OPTIONS}
              sortingMode="server"
            />
          </ListViewContainer>
        </CoreContainer>
      )}
    </>
  );
}
